import { useCallback, useEffect, useState } from 'react';
import {
  resolveInstallPromptKind,
  type InstallPromptKind,
} from '../utils/pwaEnvironment';
import {
  dismissPwaInstallPrompt,
  isPwaInstallDismissed,
  recordPwaVisit,
} from '../utils/pwaInstallPromptStorage';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function usePwaInstallPrompt(enabled: boolean) {
  const [kind, setKind] = useState<InstallPromptKind>('none');
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const resolved = resolveInstallPromptKind();
    setKind(resolved);

    if (resolved === 'none' || isPwaInstallDismissed()) return;

    const visits = recordPwaVisit();
    const showAfterMs = resolved === 'ios' ? 2500 : 1500;

    const timer = window.setTimeout(() => {
      if (!isPwaInstallDismissed()) {
        setOpen(true);
      }
    }, visits <= 1 ? showAfterMs + 2000 : showAfterMs);

    return () => window.clearTimeout(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || kind !== 'android') return;

    const onBeforeInstall = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      setDeferredPrompt(event);
      if (!isPwaInstallDismissed()) {
        setOpen(true);
      }
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, [enabled, kind]);

  const dismiss = useCallback(() => {
    dismissPwaInstallPrompt();
    setOpen(false);
  }, []);

  const installAndroid = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'accepted') {
      setOpen(false);
      return true;
    }
    return false;
  }, [deferredPrompt]);

  return {
    kind,
    open,
    canNativeInstall: Boolean(deferredPrompt),
    dismiss,
    installAndroid,
    showManual: () => setOpen(true),
  };
}
